import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Search, Loader2, Download, User, MapPin, Mail, Phone,
  ChevronLeft, ChevronRight, X, Briefcase, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { searchATSCandidates, importATSCandidate } from '../services/api';

const ATSCandidateImportModal = ({ isOpen, onClose, region, jobId, onImported }) => {
  const [search, setSearch] = useState('');
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searched, setSearched] = useState(false);
  const [searchMeta, setSearchMeta] = useState({});

  const handleSearch = async (p = 1) => {
    if (!search.trim()) { toast.error('Enter a name, email, phone, or applicant ID'); return; }
    setLoading(true);
    setSearched(true);
    try {
      const data = await searchATSCandidates(region, search, p);
      setApplicants(data.applicants || []);
      setTotalPages(data.num_pages || 1);
      setTotalCount(data.count || 0);
      setPage(data.page || p);
      setSearchMeta({ search_type: data.search_type, searched_records: data.searched_records });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally { setLoading(false); }
  };

  const handleImport = async (applicant) => {
    setImporting(applicant.ats_id);
    try {
      const result = await importATSCandidate(region, applicant.ats_id, jobId);
      toast.success(`Imported: ${result.name}`);
      // Don't close — allow importing multiple candidates
      setApplicants(prev => prev.map(a => a.ats_id === applicant.ats_id ? { ...a, imported: true } : a));
      if (onImported) onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(null); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()} data-testid="ats-candidate-import-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Import Candidate from ATS</h2>
            <p className="text-xs text-muted-foreground">Search by email (exact), applicant ID, or name</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <form onSubmit={e => { e.preventDefault(); handleSearch(1); }} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Email (exact match), applicant ID, or name..."
                className="pl-9" data-testid="ats-candidate-search-input" autoFocus />
            </div>
            <Button type="submit" disabled={loading || !search.trim()} data-testid="ats-candidate-search-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Search
            </Button>
          </form>
          {searched && totalCount > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              {totalCount.toLocaleString()} {totalCount === 1 ? 'match' : 'matches'} found
              {searchMeta.search_type === 'name' && searchMeta.searched_records > 0 && (
                <span className="text-muted-foreground/70"> · Searched most recent {searchMeta.searched_records.toLocaleString()} applicants</span>
              )}
              {searchMeta.search_type === 'email' && <span className="text-muted-foreground/70"> · Exact email match</span>}
              {searchMeta.search_type === 'id' && <span className="text-muted-foreground/70"> · ID lookup</span>}
              {totalPages > 1 && <span> · Page {page} of {totalPages}</span>}
            </p>
          )}
          {searched && totalCount === 0 && !loading && (
            <p className="text-xs text-amber-600 mt-2">
              No matches found. {searchMeta.search_type === 'name' ? 'Try using an email address for exact match.' : 'Check the search term and try again.'}
            </p>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !searched ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <User className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">Search for candidates to import from Ceipal ATS</p>
              <p className="text-xs mt-1">Email gives exact match · Name searches most recent applicants</p>
            </div>
          ) : applicants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <AlertTriangle className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">No candidates found matching "{search}"</p>
            </div>
          ) : (
            applicants.map((a) => (
              <div key={a.ats_id} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${a.imported ? 'border-green-500/30 bg-green-500/5' : 'hover:border-primary/20'}`}
                data-testid={`ats-candidate-row-${a.ats_id}`}>
                <div className="min-w-0 flex-1 mr-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{a.name || 'No Name'}</span>
                    {a.applicant_id && <Badge variant="outline" className="text-[10px]">#{a.applicant_id}</Badge>}
                    {a.source && <Badge variant="secondary" className="text-[10px]">{a.source}</Badge>}
                    {a.imported && <Badge className="bg-green-500/10 text-green-600 text-[10px]">Imported</Badge>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                    {a.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{a.email}</span>}
                    {a.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{a.phone}</span>}
                    {(a.city || a.state) && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{[a.city, a.state].filter(Boolean).join(', ')}</span>}
                  </div>
                  {a.skills && <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{a.skills}</p>}
                  {a.job_title && <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Briefcase className="h-2.5 w-2.5" />{a.job_title}</p>}
                </div>
                {a.imported ? (
                  <Badge className="bg-green-500 text-white text-xs shrink-0">Added</Badge>
                ) : (
                  <Button size="sm" className="gap-1.5 shrink-0" onClick={() => handleImport(a)}
                    disabled={importing === a.ats_id} data-testid={`import-candidate-btn-${a.ats_id}`}>
                    {importing === a.ats_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    Import
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {searched && totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => handleSearch(page - 1)}>
              <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Previous
            </Button>
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => handleSearch(page + 1)}>
              Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ATSCandidateImportModal;
